import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';

import EnquiryDetailsForm from './EnquiryDetailsForm';
import GeneralDetailsForm from './GeneralContactForm';
import NotesTab from './Notes/NotesTab';
import useContactForm from '../CustomHooks/useContactForm';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px 0',
  },
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  actionBtn: {
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
  },
  dialogContent: {
    padding: '24px !important',
  },
}));

/**
 * One dialog. For an existing contact it has three independent tabs:
 *   - General: contact + property identity.
 *   - Enquiry: this sales engagement. Locked until the backend has
 *     auto-created an enquiry record for this contact (normally
 *     triggered by a stage change made right here on the General tab).
 *   - Notes: independent of both - just needs the contact to exist.
 * All three are always editable; there's no read-only mode.
 *
 * For a brand new contact there's nothing to tab between yet - Enquiry
 * and Notes don't apply until the contact is saved - so the tab bar is
 * skipped entirely and only the General form is shown.
 *
 * General and Enquiry each save themselves - there's no combined "Save
 * everything" action, since they're genuinely separate DB writes. The
 * dialog stays open after a save so the user can move on to whichever
 * tab it just unlocked.
 */
export default function ContactFormDialog({
  open,
  onClose,
  onFetchEnquiry,
  onSaveContact,
  onSaveEnquiry,
  onAddNote,
  onDeleteNote,
  contactData = null,
  notes = [],
  notesLoading = false,
  currentUserId,
  teams = [],
  stages = [],
  teamMembers = [],
  selectedTeam = {},
  currentStage = null,       // NEW
  currentUserEmail = '',     // NEW
}) {
  const classes = useStyles();

  const form = useContactForm({
    open,
    contactData,
    teams,
    stages,
    teamMembers,
    selectedTeam,
    currentStage,
    currentUserEmail, 
    onFetchEnquiry,
    onSaveContact,
    onSaveEnquiry,
    onClose,
    onEnquiryAutoCreated: () => {
      // Hook for a toast/snackbar at the call site, e.g.:
      // enqueueSnackbar('Enquiry created - add the details below', { variant: 'success' });
    },
    onEnquiryFetchError: () => {
      // Hook for a toast/snackbar at the call site, e.g.:
      // enqueueSnackbar("Couldn't load enquiry details", { variant: 'error' });
    },
  });

  const {
    activeTab,
    setActiveTab,
    cancelEditing,
    general,
    enquiry,
    isEdit,
    isEnquiryUnlocked,
    isEnquiryLoading,
    enquiryFetchError,
    isNotesUnlocked,
  } = form;

  const handleCancel = () => {
    // Existing record: drop unsaved changes but keep the dialog open.
    // Brand new, never-saved contact has nothing to revert to, so
    // Cancel really does mean close.
    if (isEdit) {
      cancelEditing();
    } else {
      onClose();
    }
  };

  const renderFooter = () => {
    if (activeTab === 'notes') {
      return (
        <Button onClick={onClose} variant="outlined" color="inherit" className={classes.actionBtn}>
          Close
        </Button>
      );
    }

    if (activeTab === 'enquiry') {
      return (
        <>
          <Button onClick={handleCancel} variant="outlined" color="inherit" className={classes.actionBtn}>
            Cancel
          </Button>
          <Button
            onClick={enquiry.submit}
            variant="contained"
            color="primary"
            disabled={enquiry.saving}
            className={classes.actionBtn}>
            {enquiry.saving ? 'Saving...' : 'Save Enquiry'}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button onClick={handleCancel} variant="outlined" color="inherit" className={classes.actionBtn}>
          Cancel
        </Button>
        <Button
          onClick={general.submit}
          variant="contained"
          color="primary"
          disabled={general.saving}
          className={classes.actionBtn}>
          {general.saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Contact'}
        </Button>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle} component="div">
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          {isEdit ? 'Edit Contact' : 'Add Contact'}
        </Typography>
        <Box className={classes.titleActions}>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {isEdit && (
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          style={{ paddingLeft: 24, borderBottom: '1px solid #e0e0e0' }}>
          <Tab value="general" label="General" />
          <Tab
            value="enquiry"
            label={
              isEnquiryLoading ? (
                <Box display="flex" alignItems="center" gap="6px">
                  <CircularProgress size={14} />
                  <span>Enquiry</span>
                </Box>
              ) : enquiryFetchError ? (
                <Tooltip title="Couldn't load enquiry details - try reopening this contact">
                  <span>Enquiry</span>
                </Tooltip>
              ) : isEnquiryUnlocked ? (
                'Enquiry'
              ) : (
                <Tooltip title="Enquiry details become available once this contact reaches the Enquiry stage">
                  <span>Enquiry</span>
                </Tooltip>
              )
            }
            disabled={!isEnquiryUnlocked || isEnquiryLoading}
          />
          <Tab
            value="notes"
            label={
              isNotesUnlocked ? (
                'Notes'
              ) : (
                <Tooltip title="Save this contact first to start adding notes">
                  <span>Notes</span>
                </Tooltip>
              )
            }
            disabled={!isNotesUnlocked}
          />
        </Tabs>
      )}

      <DialogContent className={classes.dialogContent} dividers>
        {(!isEdit || activeTab === 'general') && (
          <GeneralDetailsForm
            formData={general.formData}
            errors={general.errors}
            onChange={general.handleChange}
            onStageChange={general.handleStageChange}
            onDropPinChange={general.handleDropPinChange}
            teams={teams}
            stages={stages}
            teamMembers={teamMembers}
            isEdit={isEdit}
          />
        )}

        {isEdit && activeTab === 'enquiry' && isEnquiryUnlocked && (
          <EnquiryDetailsForm
            formData={enquiry.formData}
            errors={enquiry.errors}
            onChange={enquiry.handleChange}
          />
        )}

        {isEdit && activeTab === 'notes' && isNotesUnlocked && (
          <NotesTab
            notes={notes}
            loading={notesLoading}
            onAddNote={onAddNote}
            onDeleteNote={onDeleteNote}
            currentUserId={currentUserId}
          />
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>{renderFooter()}</DialogActions>
    </Dialog>
  );
}